import { jsPDF } from "jspdf";
import { cleanBullets, contactLine, dates, fileName, templateById } from "@/lib/resume";

const STYLE = {
  modern: { font: "helvetica", header: "left", section: "rule" },
  professional: { font: "times", header: "center", section: "double" },
  minimal: { font: "helvetica", header: "left", section: "plain" },
  creative: { font: "helvetica", header: "band", section: "chip" },
  healthcare: { font: "helvetica", header: "left", section: "bar" },
};

export const buildPdf = (r, templateId) => {
  const t = templateById(templateId);
  const s = STYLE[t.id];
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight(), M = 48, CW = W - M * 2;
  let y = M;
  const [ar, ag, ab] = t.accent;
  const ink = () => doc.setTextColor(30, 30, 30);
  const font = (st = "normal", size = 10) => { doc.setFont(s.font, st); doc.setFontSize(size); };
  const need = (h) => { if (y + h > H - M) { doc.addPage(); y = M; } };
  const para = (txt, { size = 10, st = "normal", indent = 0, gap = 3, color } = {}) => {
    if (!txt) return;
    font(st, size); color ? doc.setTextColor(...color) : ink();
    doc.splitTextToSize(txt, CW - indent).forEach((ln) => { need(size + gap); doc.text(ln, M + indent, y + size); y += size + gap; });
  };
  const rowLR = (left, right, { size = 10.5, st = "bold" } = {}) => {
    need(size + 4); font(st, size); ink();
    const rw = right ? (font("normal", 9.5), doc.getTextWidth(right)) : 0;
    font(st, size);
    const l = doc.splitTextToSize(left, CW - rw - 12)[0] || "";
    doc.text(l, M, y + size);
    if (right) { font("normal", 9.5); doc.setTextColor(90, 90, 90); doc.text(right, W - M, y + size, { align: "right" }); }
    y += size + 4;
  };
  const section = (title) => {
    need(40); y += 12;
    const T = title.toUpperCase();
    if (s.section === "bar") { doc.setFillColor(ar, ag, ab); doc.rect(M, y, 3, 13, "F"); font("bold", 11); doc.setTextColor(ar, ag, ab); doc.text(T, M + 9, y + 10.5); y += 20; }
    else if (s.section === "chip") { font("bold", 10.5); doc.setTextColor(ar, ag, ab); doc.text(T, M, y + 10); doc.setDrawColor(ar, ag, ab); doc.setLineWidth(2); doc.line(M, y + 15, M + 28, y + 15); y += 24; }
    else {
      font("bold", s.section === "plain" ? 10 : 11); s.section === "rule" ? doc.setTextColor(ar, ag, ab) : ink();
      doc.text(T, M, y + 10, s.section === "plain" ? { charSpace: 1.2 } : undefined);
      if (s.section !== "plain") { doc.setDrawColor(s.section === "rule" ? ar : 40, s.section === "rule" ? ag : 40, s.section === "rule" ? ab : 40); doc.setLineWidth(s.section === "double" ? 0.8 : 0.6); doc.line(M, y + 15, W - M, y + 15); if (s.section === "double") doc.line(M, y + 17.5, W - M, y + 17.5); }
      y += s.section === "plain" ? 18 : 24;
    }
  };
  const p = r.personal;
  const contact = contactLine(p).join("   |   ");
  if (s.header === "band") {
    doc.setFillColor(ar, ag, ab); doc.rect(0, 0, W, 96, "F");
    font("bold", 24); doc.setTextColor(255, 255, 255); doc.text(p.full_name || "Your Name", M, 44);
    font("normal", 12); doc.text(p.title || "", M, 62);
    font("normal", 9); doc.text(doc.splitTextToSize(contact, CW)[0] || "", M, 80);
    y = 112;
  } else {
    const c = s.header === "center", x = c ? W / 2 : M, o = c ? { align: "center" } : undefined;
    font("bold", c ? 22 : 24); ink(); doc.text(p.full_name || "Your Name", x, y + 20, o); y += 28;
    if (p.title) { font("normal", 12); s.header === "left" && t.id !== "minimal" ? doc.setTextColor(ar, ag, ab) : doc.setTextColor(70, 70, 70); doc.text(p.title, x, y + 12, o); y += 18; }
    font("normal", 9); doc.setTextColor(90, 90, 90);
    doc.splitTextToSize(contact, CW).forEach((ln) => { doc.text(ln, x, y + 10, o); y += 12; });
    if (t.id === "modern") { doc.setFillColor(ar, ag, ab); doc.rect(M, y + 6, 48, 3, "F"); y += 12; }
  }
  const certsFirst = t.id === "healthcare";
  const certs = () => { if (!r.certifications.some((c) => c.name)) return; section(certsFirst ? "Licenses & Certifications" : "Certifications"); r.certifications.filter((c) => c.name).forEach((c) => rowLR([c.name, c.issuer].filter(Boolean).join(" — "), c.date, { size: 10, st: "normal" })); };
  if (r.summary) { section("Summary"); para(r.summary); }
  if (certsFirst) certs();
  if (r.experience.length) {
    section("Experience");
    r.experience.forEach((e) => {
      need(40); rowLR(e.role || "Role", dates(e));
      para([e.company, e.location].filter(Boolean).join(" · "), { size: 10, st: "italic", color: [80, 80, 80] });
      cleanBullets(e.bullets).forEach((b) => {
        font("normal", 10); const ls = doc.splitTextToSize(b, CW - 14);
        ls.forEach((ln, i) => { need(13); ink(); if (!i) doc.text("•", M + 3, y + 10); doc.text(ln, M + 14, y + 10); y += 13; });
      });
      y += 6;
    });
  }
  if (r.education.length) {
    section("Education");
    r.education.forEach((e) => { rowLR(e.degree || "Degree", dates(e)); para([e.school, e.location].filter(Boolean).join(" · "), { size: 10, st: "italic", color: [80, 80, 80] }); para(e.details, { size: 9.5 }); y += 4; });
  }
  if (r.skills.length) { section("Skills"); para(r.skills.join(" · ")); }
  if (!certsFirst) certs();
  doc.setProperties({ title: `${p.full_name || "Resume"} — Resume`, subject: "Resume", creator: "RPOPRO.co Resume Studio" });
  return doc;
};

export const downloadPdf = (r, t, job) => buildPdf(r, t).save(fileName(r, job));
export const pdfFile = (r, t, job) => new File([buildPdf(r, t).output("blob")], fileName(r, job), { type: "application/pdf" });
